export type InchaustiFileCategory =
  | "quantification"
  | "annotation"
  | "gene-set"
  | "supporting";

export type InchaustiFile = {
  name: string;
  category: InchaustiFileCategory;
  bytes: number;
  md5: string;
  descriptionEn: string;
  descriptionZh: string;
};

const zenodoContentBase =
  "https://zenodo.org/api/records/18760090/files";

export const inchaustiAudit = {
  schemaVersion: "1.0.0",
  retrieved: "2026-07-26",
  title:
    "Single-cell RNA-seq reveals trans-sialidase-like superfamily gene expression heterogeneity in Trypanosoma cruzi populations",
  sources: {
    paper: "https://pmc.ncbi.nlm.nih.gov/articles/PMC13056360/",
    doi: "https://doi.org/10.7554/eLife.105822",
    bioProject: "https://www.ncbi.nlm.nih.gov/bioproject/PRJNA1200704",
    enaRun: "https://www.ebi.ac.uk/ena/browser/view/SRR31781067",
    bioSample: "https://www.ebi.ac.uk/ena/browser/view/SAMN45910119",
    zenodo: "https://zenodo.org/records/18760090",
    code: "https://github.com/bioinfo-iibce/scRNAseq_Tcruzi",
  },
  paper: {
    published: "2026-04-07",
    pmcid: "PMC13056360",
    cellsAfterQualityFilteringBeforeDoubletRemoval: 3192,
    cellsRetainedAfterDoubletRemoval: 3151,
    detectedGenes: 14321,
    annotatedProteinCodingGenes: 15319,
    meanGenesPerCell: 1088,
    meanUmisPerCell: 2461,
    doubletsRemoved: 41,
    reportedClusters: [
      {
        id: 0,
        labelEn: "Cell-derived trypomastigotes",
        labelZh: "细胞来源锥鞭毛体",
        cellsBeforeDoubletRemoval: 2201,
        marker: "C4B63-16g183",
      },
      {
        id: 1,
        labelEn: "Amastigotes",
        labelZh: "无鞭毛体",
        cellsBeforeDoubletRemoval: 824,
        marker: "C4B63-16g155",
      },
      {
        id: 2,
        labelEn: "Putative amastigote–trypomastigote transition",
        labelZh: "推定无鞭毛体—锥鞭毛体过渡细胞",
        cellsBeforeDoubletRemoval: 167,
        marker: null,
      },
    ],
  },
  experimentalSource: {
    parasiteStages:
      "Amastigotes and cell-derived trypomastigotes from infected host-cell culture",
    hostSpecies: "Rattus norvegicus",
    hostCellLine: "H9c2 rat myoblasts",
    hostCellLineAccession: "ATCC CRL-1446",
    boundary:
      "The paper's infected-H9c2 experimental source and the SRA repository field “isolation source: Axenic culture” are different metadata layers. The repository wording is preserved but is not used as the biological sample origin.",
  },
  rawSequencing: {
    bioProject: "PRJNA1200704",
    bioSample: "SAMN45910119",
    secondarySample: "SRS23597921",
    experiment: "SRX27142470",
    run: "SRR31781067",
    sampleAlias: "T. cruzi amastigotes and trypomastigotes",
    organism: "Trypanosoma cruzi",
    strain: "DM28c",
    isolationSource: "Axenic culture",
    isolationSourceBoundary:
      "Repository-supplied SRA field; insufficient on its own to describe the infected-H9c2 experimental source",
    instrumentPlatform: "ILLUMINA",
    instrumentModel: "Illumina HiSeq 2500",
    libraryLayout: "PAIRED",
    libraryStrategy: "RNA-Seq",
    librarySource: "TRANSCRIPTOMIC SINGLE CELL",
    librarySelection: "PolyA",
    readCount: 882316647,
    baseCount: 104995680993,
    firstPublic: "2026-02-22",
    fastq: [
      {
        name: "SRR31781067_1.fastq.gz",
        bytes: 19989510573,
        md5: "879d363cbc29f7b2f9dc7300daedae3b",
        url: "https://ftp.sra.ebi.ac.uk/vol1/fastq/SRR317/067/SRR31781067/SRR31781067_1.fastq.gz",
      },
      {
        name: "SRR31781067_2.fastq.gz",
        bytes: 41306757344,
        md5: "743876f2279637e09f568f465e466454",
        url: "https://ftp.sra.ebi.ac.uk/vol1/fastq/SRR317/067/SRR31781067/SRR31781067_2.fastq.gz",
      },
    ],
  },
  quantification: {
    reference:
      "T. cruzi Dm28c 2018 genome, TriTrypDB release 62, combined with Dm28c maxicircle kDNA",
    referenceZh:
      "T. cruzi Dm28c 2018基因组（TriTrypDB release 62）与Dm28c大环kDNA组合参考",
    utrAnnotation:
      "11,362 coding-sequence 3′ UTR regions annotated with peaks2UTR",
    utrAnnotationZh: "使用peaks2UTR为11,362个编码序列标注3′ UTR",
    method: "kallisto bustools pseudoalignment",
    options: ["--filter", "--em"],
    note:
      "The --em option can produce fractional expected counts; the deposited Matrix Market files declare real-valued entries.",
    noteZh:
      "--em选项可产生小数形式的期望计数；作者提交的Matrix Market文件声明为实数型条目。",
    matrices: [
      {
        name: "cells_x_genes.mtx",
        barcodeFile: "cells_x_genes.barcodes.txt",
        geneFile: "cells_x_genes.genes.txt",
        barcodeRows: 82818,
        geneColumns: 19132,
        nonZeroEntries: 16359799,
      },
      {
        name: "cells_x_genes_2.mtx",
        barcodeFile: "cells_x_genes.barcodes_2.txt",
        geneFile: "cells_x_genes.genes_2.txt",
        barcodeRows: 82813,
        geneColumns: 19132,
        nonZeroEntries: 16337978,
      },
    ],
    commonBarcodesMergedByPublicCode: 82751,
    geneListsIdentical: true,
  },
  methodAudit: {
    paperThresholds: {
      nUMI: "> 1,200",
      nGene: "> 100",
      log10GenesPerUMI: "> 0.8",
      mitoRatio: "< 0.1",
    },
    publicCodeThresholds: {
      nUMI: "> 3,000",
      nGene: "> 130",
      log10GenesPerUMI: "> 0.8",
      mitoRatio: "< 0.1",
    },
    status:
      "The nUMI and nGene thresholds differ between the version-of-record Methods and the current public R script. TrypCell Atlas does not choose between them; author confirmation is required before a reproducible rerun is labelled as the paper workflow.",
    statusZh:
      "正式论文方法与当前公开R脚本的nUMI和nGene阈值不同。TrypCell Atlas不替作者选择；在把重算结果标记为“论文流程”之前，需要作者确认。",
    clusterCountBoundary:
      "The paper reports 3,192 post-QC transcriptomes and cluster counts summing to 3,192, then states that 41 doublets—all in cluster 2—were removed and 3,151 singlets retained for downstream analysis. No final post-removal cluster-count table or reusable Seurat/H5AD object is deposited in Zenodo record 18760090.",
    clusterCountBoundaryZh:
      "论文报告3,192个质量过滤后转录组，三个cluster的计数总和也是3,192；随后又说明移除了41个双细胞（全部来自cluster 2），下游保留3,151个singlet。Zenodo 18760090没有提交移除后的最终cluster计数表，也没有可复用的Seurat/H5AD对象。",
    portalStatus:
      "Source provenance and deposited matrices are indexed. A reconstructed UMAP is not published in TrypCell Atlas because author UMAP coordinates and final cell metadata are not deposited.",
  },
  codeAudit: {
    repositoryRevision: "0396b4d7b3e3089b302997166e76dc69b0423d1e",
    script: "final_code_scRNAseq_Tcruzi.R",
    scriptSha256:
      "d1f6d4bf875119b00b4b57d28e806ac422c927d0152f8e2134751f893cf0d591",
    readmeSha256:
      "bc5e549404b4ea8700c4f0bd6828d709be457e18ae1862f61b1a916dab9d4d81",
  },
  zenodo: {
    record: 18760090,
    title: "scRNA-seq Analysis of Trypanosoma cruzi Populations",
    license: "CC BY 4.0",
    published: "2026-02-24",
    fileCount: 17,
    totalBytes: 461333115,
    files: [
      {
        name: "cells_x_genes.genes.txt",
        category: "quantification",
        bytes: 241515,
        md5: "01def374155e736b8baaf262ebd398d3",
        descriptionEn: "Feature list for technical matrix 1",
        descriptionZh: "技术矩阵1的基因列表",
      },
      {
        name: "cells_x_genes.barcodes.txt",
        category: "quantification",
        bytes: 1407906,
        md5: "98b29e5a8732c7eefc5a242b1b442a49",
        descriptionEn: "82,818 barcodes for technical matrix 1",
        descriptionZh: "技术矩阵1的82,818个条形码",
      },
      {
        name: "cells_x_genes.genes_2.txt",
        category: "quantification",
        bytes: 241515,
        md5: "01def374155e736b8baaf262ebd398d3",
        descriptionEn: "Feature list for technical matrix 2",
        descriptionZh: "技术矩阵2的基因列表",
      },
      {
        name: "anotacion_dm28c_con_mt.gff",
        category: "annotation",
        bytes: 14498432,
        md5: "c020676e448481890ee9908fbfbd900c",
        descriptionEn: "Dm28c annotation including mitochondrial features",
        descriptionZh: "包含线粒体特征的Dm28c注释",
      },
      {
        name: "cells_x_genes.barcodes_2.txt",
        category: "quantification",
        bytes: 1407821,
        md5: "3cd4554c077ccd0b29b401d3f2ebc7fd",
        descriptionEn: "82,813 barcodes for technical matrix 2",
        descriptionZh: "技术矩阵2的82,813个条形码",
      },
      {
        name: "cells_x_genes.mtx",
        category: "quantification",
        bytes: 221226276,
        md5: "0320356e1f31f25fba12ef6315033b89",
        descriptionEn: "82,818 × 19,132 real-valued sparse matrix",
        descriptionZh: "82,818 × 19,132实数型稀疏矩阵",
      },
      {
        name: "cells_x_genes_2.mtx",
        category: "quantification",
        bytes: 221482578,
        md5: "e464a00e3e2aa1771425fdeab4fc68d3",
        descriptionEn: "82,813 × 19,132 real-valued sparse matrix",
        descriptionZh: "82,813 × 19,132实数型稀疏矩阵",
      },
      {
        name: "Dm28c_disruptive_compartment_positions.bed",
        category: "annotation",
        bytes: 742,
        md5: "7330f2520f42b87c43d700791b0927cd",
        descriptionEn: "Disruptive genomic-compartment intervals",
        descriptionZh: "扰动基因组区室范围",
      },
      {
        name: "phosphatases_dm28c.csv",
        category: "gene-set",
        bytes: 7913,
        md5: "2f10d1253e3757b815a5d261bd5113ac",
        descriptionEn: "Dm28c phosphatase gene set",
        descriptionZh: "Dm28c磷酸酶基因集",
      },
      {
        name: "polymerase_dm28c.csv",
        category: "gene-set",
        bytes: 5397,
        md5: "d94b73e1ad05ec0e226e72dd6ec192c4",
        descriptionEn: "Dm28c polymerase gene set",
        descriptionZh: "Dm28c聚合酶基因集",
      },
      {
        name: "tcs_high.txt",
        category: "gene-set",
        bytes: 297,
        md5: "3c973e078680fd2a8f9a7a5921b53d97",
        descriptionEn: "Frequently detected TcS subset",
        descriptionZh: "高频检出的TcS子集",
      },
      {
        name: "tcs_low_all.txt",
        category: "gene-set",
        bytes: 8216,
        md5: "2edf94f9c9c37f8e2370242f29cf98f9",
        descriptionEn: "Full low-detection TcS set",
        descriptionZh: "完整低检出TcS基因集",
      },
      {
        name: "tcs_low.txt",
        category: "gene-set",
        bytes: 136,
        md5: "a455ee11ee0aca1db0bc815a5d5a87a3",
        descriptionEn: "Sampled low-detection TcS subset",
        descriptionZh: "抽样低检出TcS子集",
      },
      {
        name: "transporters_dm28c.csv",
        category: "gene-set",
        bytes: 29121,
        md5: "2914a939391254f939b018b4a49447da",
        descriptionEn: "Dm28c transporter gene set",
        descriptionZh: "Dm28c转运蛋白基因集",
      },
      {
        name: "Ribsoomal_RNA.csv",
        category: "gene-set",
        bytes: 2445,
        md5: "d993afccee1ae581ab0e423cc65b0ae2",
        descriptionEn: "rRNA exclusion list (deposited filename retained)",
        descriptionZh: "rRNA排除列表（保留作者文件名）",
      },
      {
        name: "abundance_tripos_1.txt",
        category: "supporting",
        bytes: 771401,
        md5: "b6bcef4112974c54289048938e085835",
        descriptionEn: "Supporting trypomastigote abundance data",
        descriptionZh: "锥鞭毛体丰度支持数据",
      },
      {
        name: "Dm28c_core_compartment_positions.bed",
        category: "annotation",
        bytes: 1404,
        md5: "2b2f3a20c0b456ffd3648e7bd8b6ea77",
        descriptionEn: "Core genomic-compartment intervals",
        descriptionZh: "核心基因组区室范围",
      },
    ] satisfies InchaustiFile[],
  },
};

export function inchaustiFileUrl(file: InchaustiFile) {
  return `${zenodoContentBase}/${encodeURIComponent(file.name)}/content`;
}

export function getExpandedInchaustiAudit() {
  return {
    ...inchaustiAudit,
    zenodo: {
      ...inchaustiAudit.zenodo,
      files: inchaustiAudit.zenodo.files.map((file) => ({
        ...file,
        downloadUrl: inchaustiFileUrl(file),
      })),
    },
  };
}
